// src/lib/evidenceAccess.ts
// ACTION 2 — the only client-side entry point for reading evidence objects.
// The browser never touches the storage bucket directly; it submits an evidence
// reference and the server derives the key, checks authorization and signs.
import { supabase } from "@/integrations/supabase/client";

export type EvidenceRef = {
  evidenceId?: string;
  legacyEvidenceId?: string;
  /** Legacy/wizard raw storage key. Must be attached to an accessible assessment. */
  storageKey?: string;
};

export type EvidenceAccessResult = {
  url: string;
  expires_in: number;
  filename: string;
  mime: string;
  disposition: "inline" | "attachment";
  scan_status: string;
};

type CacheEntry = { value: EvidenceAccessResult; expiresAt: number };

// Session-scoped, in-memory only. Signed URLs are never persisted to storage,
// state that outlives the tab, logs, reports or the database.
const cache = new Map<string, CacheEntry>();

function cacheKey(ref: EvidenceRef, disposition: string) {
  return `${ref.evidenceId ?? ""}|${ref.legacyEvidenceId ?? ""}|${ref.storageKey ?? ""}|${disposition}`;
}

export async function getEvidenceAccess(
  ref: EvidenceRef,
  opts: { disposition?: "inline" | "attachment"; ttl?: number } = {},
): Promise<EvidenceAccessResult> {
  const disposition = opts.disposition ?? "inline";
  const key = cacheKey(ref, disposition);
  const hit = cache.get(key);
  if (hit && hit.expiresAt > Date.now() + 5_000) return hit.value;

  const { data, error } = await supabase.functions.invoke("evidence-access", {
    body: {
      evidence_id: ref.evidenceId,
      legacy_evidence_id: ref.legacyEvidenceId,
      storage_key: ref.storageKey,
      disposition,
      ttl: opts.ttl,
    },
  });

  if (error || !data?.url) {
    throw new Error(data?.error || error?.message || "Evidence unavailable");
  }

  const result = data as EvidenceAccessResult;
  // Expire the cache slightly before the URL itself does.
  cache.set(key, {
    value: result,
    expiresAt: Date.now() + Math.max((result.expires_in ?? 300) - 15, 30) * 1000,
  });
  return result;
}

export async function getEvidenceUrl(
  ref: EvidenceRef,
  opts: { disposition?: "inline" | "attachment"; ttl?: number } = {},
): Promise<string> {
  return (await getEvidenceAccess(ref, opts)).url;
}

export async function openEvidence(
  ref: EvidenceRef,
  opts: { disposition?: "inline" | "attachment" } = {},
) {
  const url = await getEvidenceUrl(ref, opts);
  window.open(url, "_blank", "noopener,noreferrer");
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Interpret a stored evidence value: a bare UUID is an evidence record ID,
 *  anything else is treated as a legacy storage key / URL. */
export function refFromValue(value: string): EvidenceRef {
  const v = (value ?? "").trim();
  return UUID_RE.test(v) ? { evidenceId: v } : { storageKey: v };
}

/** Back-compat shim for call sites that still hold a raw storage key or ID. */
export async function getSignedUrlForKey(
  value: string,
  disposition: "inline" | "attachment" = "inline",
): Promise<string> {
  return getEvidenceUrl(refFromValue(value), { disposition });
}


export function clearEvidenceUrlCache() {
  cache.clear();
}
